import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  CreateInvoiceProfileDto,
  CreateInvoiceDto,
  CreateInvoiceItemDto,
  InvoiceItemData,
  InvoiceListItem,
  InvoicePageResult,
  InvoiceProfilePageResult,
  InvoiceProfileOption,
  InvoiceProfileQueryParams,
  InvoiceQueryParams,
  QueryInvoiceReceiptCandidatesDto,
  ReceiptCandidateItem,
  SyncInvoiceApprovalStatusResult,
  UpdateInvoiceProfileDto,
  UpdateInvoiceDto,
} from './types/invoice';

const request = createAppRequest();
const BASE_PATH = '/bff/zy-crm/invoices';
const PROFILE_PATH = '/bff/zy-crm/invoice-profiles';

export async function queryInvoices(params: InvoiceQueryParams, options?: ApiRequestOptions) {
  return request<InvoicePageResult>(BASE_PATH, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getInvoiceDetail(uuid: string, options?: ApiRequestOptions) {
  return request<InvoiceListItem>(`${BASE_PATH}/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createInvoice(payload: CreateInvoiceDto, options?: ApiRequestOptions) {
  return request<InvoiceListItem>(BASE_PATH, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function updateInvoice(uuid: string, payload: UpdateInvoiceDto, options?: ApiRequestOptions) {
  return request<InvoiceListItem>(`${BASE_PATH}/${uuid}`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteInvoice(uuid: string, options?: ApiRequestOptions) {
  return request<void>(`${BASE_PATH}/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function syncInvoiceApprovalStatus(
  payload: { uuids: string[] },
  options?: ApiRequestOptions,
) {
  return request<SyncInvoiceApprovalStatusResult>(`${BASE_PATH}/sync-approval-status`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function queryReceiptCandidates(
  params: QueryInvoiceReceiptCandidatesDto,
  options?: ApiRequestOptions,
) {
  return request<ReceiptCandidateItem[]>(`${BASE_PATH}/receipt-candidates`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function queryInvoiceProfiles(params: { page?: number; pageSize?: number; keyword?: string } = {}, options?: ApiRequestOptions) {
  return request<InvoiceProfilePageResult>(PROFILE_PATH, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getInvoiceProfileDetail(uuid: string, options?: ApiRequestOptions) {
  return request<InvoiceProfileOption>(`${PROFILE_PATH}/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function queryInvoiceProfilePage(
  params: InvoiceProfileQueryParams,
  options?: ApiRequestOptions,
) {
  return request<InvoiceProfilePageResult>(PROFILE_PATH, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function createInvoiceProfile(
  payload: CreateInvoiceProfileDto,
  options?: ApiRequestOptions,
) {
  return request<InvoiceProfileOption>(PROFILE_PATH, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function updateInvoiceProfile(
  uuid: string,
  payload: UpdateInvoiceProfileDto,
  options?: ApiRequestOptions,
) {
  return request<InvoiceProfileOption>(`${PROFILE_PATH}/${uuid}`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteInvoiceProfile(uuid: string, options?: ApiRequestOptions) {
  return request<void>(`${PROFILE_PATH}/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function batchUpdateInvoiceProfileStatus(
  payload: { uuids: string[]; status: number },
  options?: ApiRequestOptions,
) {
  return request<void>(`${PROFILE_PATH}/batch-status`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export type { InvoiceItemData, InvoiceProfileOption, CreateInvoiceItemDto };
